import { useState, useEffect, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import type { SiteDeviceWithSpec } from '@shared/types'

// ═══════════════════════════════════════════════════════════
// AMBER PORTAL — SITE NETWORK VIEW
// Switch Port Visualization · Sparklines · Drag-Drop VLANs
// Cable Path Tracing · Port Detail Drawer
// ═══════════════════════════════════════════════════════════

// ── Types ────────────────────────────────────────────────

interface Props {
  siteId: string
  onOpenDevice?: (slug: string) => void
  /** Pre-select this switch by ID (used when opening from modal) */
  initialSwitchId?: string
  /** Pre-select this port number (used when navigating from a wired client) */
  highlightPort?: number
}

interface PortData {
  number: number
  label: string | null
  name: string | null
  type: string
  ip: string | null
  vlan: number | null
  poe: number | null
  speed: string | null
  link: boolean
  bwBase: number
  bw: number[]
  pp: { panel: number; port: number } | null
  cable: string | null
  deviceSlug: string | null
  interfaceId: string | null
}

interface SwitchInfo {
  id: string
  slug: string
  name: string
  model: string | null
  ip: string | null
  mac: string | null
  uptime: string | null
  firmware: string | null
  poeBudget: number
  totalPorts: number
  isLive: boolean
}

interface PortApiRecord {
  id: string
  slug: string
  port_number: number
  port_label?: string | null
  port_type?: string | null
  name: string
  type: string
  speed: string | null
  poe_capable: number
  poe_watts_budget: number | null
  poe_watts_draw: number | null
  poe_power_draw: number | null
  poe_enabled: number | null
  poe_class: string | null
  is_up: number | null
  status: string
  connected_device_id: string | null
  connected_interface_id: string | null
  connected_device_mac: string | null
  connected_device_name: string | null
  connected_device_ip: string | null
  cable_id: string | null
  vlan_id: string | null
  notes: string | null
  sort_order: number
  connected_device_label: string | null
  connected_device_hostname: string | null
  connected_spec_brand: string | null
  connected_spec_model: string | null
  connected_spec_category: string | null
  connected_poe_draw: number | null
  rx_bytes: number | null
  tx_bytes: number | null
  last_synced_at: string | null
}

type ViewMode = 'status' | 'poe' | 'vlan' | 'speed' | 'bandwidth'

// ── Constants ────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  camera: 'var(--color-camera)', networking: 'var(--color-networking)', access: 'var(--color-access)',
  phone: 'var(--color-cable)', uplink: 'var(--color-infra)', power: 'var(--color-power)', empty: '#1e2028',
}

const VLAN_COLORS: Record<number, string> = {
  1: 'var(--color-infra)', 100: 'var(--color-cable)', 104: 'var(--color-camera)', 105: 'var(--color-access)',
}

const VLANS = [
  { id: 1, name: 'Default', color: 'var(--color-infra)' },
  { id: 100, name: 'Voice', color: 'var(--color-cable)' },
  { id: 104, name: 'Cameras', color: 'var(--color-camera)' },
  { id: 105, name: 'Access Control', color: 'var(--color-access)' },
]

const VIEW_LABELS: Record<ViewMode, string> = {
  status: 'Type', poe: 'PoE', vlan: 'VLAN', speed: 'Speed', bandwidth: 'BW',
}

const SPEED_COLORS: Record<string, string> = {
  '1G': 'var(--ok)', '2.5G': 'var(--info)', '10G': 'var(--accent)', '100M': 'var(--color-infra)',
}

// ── Helpers ──────────────────────────────────────────────

function rnd(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function genBW(base: number, offline: boolean): number[] {
  const v = Math.floor(base * 0.3)
  if (offline) return Array(20).fill(0).map((_, i) => i < 5 ? base + rnd(-v, v) : 0)
  return Array(20).fill(0).map(() => Math.max(0, base + rnd(-v, v)))
}

function mapCategoryToType(category: string | null): string {
  if (!category) return 'empty'
  const lower = category.toLowerCase()
  if (lower.includes('camera') || lower === 'nvr' || lower === 'vms_server') return 'camera'
  if (lower.includes('switch') || lower.includes('access_point') || lower.includes('router')) return 'networking'
  if (lower.includes('reader') || lower.includes('controller')) return 'access'
  if (lower.includes('phone') || lower.includes('intercom')) return 'phone'
  if (lower.includes('ups') || lower.includes('pdu')) return 'power'
  return 'camera'
}

function mapStatusToUplink(status: string): boolean {
  return status === 'uplink'
}

// ── Sparkline ────────────────────────────────────────────

function Spark({ data, w = 60, h = 18, color = 'var(--ok)', anim = true }: {
  data: number[]
  w?: number
  h?: number
  color?: string
  anim?: boolean
}) {
  if (!data?.length) return null
  const max = Math.max(...data, 1)
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - (v / max) * (h - 2)}`).join(' ')
  const fill = `0,${h} ${pts} ${w},${h}`
  const id = `sg${color.replace('#', '')}${w}${h}`
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={fill} fill={`url(#${id})`} />
      <polyline
        points={pts} fill="none" stroke={color} strokeWidth={1.5}
        strokeLinecap="round" strokeLinejoin="round"
        style={anim ? { strokeDasharray: w * 2, strokeDashoffset: w * 2, animation: 'sparkDraw 1s ease forwards' } : {}}
      />
      <circle cx={w} cy={h - (data[data.length - 1] / max) * (h - 2)} r={2} fill={color}>
        <animate attributeName="r" values="2;3;2" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  )
}

// ── Cable Path ───────────────────────────────────────────

function CablePath({ port, switchName }: { port: PortData; switchName: string }) {
  if (!port.pp) return null
  const off = !port.link
  return (
    <div>
      <div style={S.secTitle}>CABLE PATH</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '8px 0' }}>
        <CNode
          label={<>{switchName} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>P{port.number}</span></>}
          color={TYPE_COLORS[port.type]}
        />
        <CLine color={off ? 'var(--err)' : 'var(--accent)'} label={port.cable} alive={!off} />
        <CNode
          label={<>PP-{port.pp.panel} <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-power)' }}>P{port.pp.port}</span></>}
          color="var(--color-power)"
        />
        <CLine color={off ? 'var(--err)' : 'var(--ok)'} alive={!off} />
        <CNode
          label={<span style={{ fontFamily: 'var(--font-mono)', color: off ? 'var(--err)' : 'var(--accent)' }}>{port.label}</span>}
          color={off ? 'var(--err)' : TYPE_COLORS[port.type]}
          alert={off}
        />
      </div>
      {off && (
        <div style={{ fontSize: 10, color: 'var(--err)', display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
          <span className="snv-status-pulse-red" style={{ width: 5, height: 5, borderRadius: 3, background: 'var(--err)', display: 'inline-block' }} />
          Device offline — check cable at PP-{port.pp.panel} Port {port.pp.port}
        </div>
      )}
    </div>
  )
}

function CNode({ label, color, alert }: { label: React.ReactNode; color: string; alert?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 48 }}>
      <div style={{ width: 12, height: 12, borderRadius: 6, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {alert && <span style={{ fontSize: 6, color: '#fff', fontWeight: 900 }}>!</span>}
      </div>
      <div style={{ fontSize: 8, color: 'var(--text-tertiary)', textAlign: 'center', whiteSpace: 'nowrap' }}>{label}</div>
    </div>
  )
}

function CLine({ color, label, alive }: { color: string; label?: string | null; alive: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 24, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ height: 2, borderRadius: 1, background: color, position: 'relative', overflow: 'hidden' }}>
        {alive && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '30%', height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            animation: 'snvCablePulse 1.5s ease-in-out infinite',
          }} />
        )}
      </div>
      {label && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 7, color: 'var(--text-disabled)', textAlign: 'center', marginTop: 1 }}>{label}</div>}
    </div>
  )
}

// ── Legend Item ───────────────────────────────────────────

function Lg({ c, l, b }: { c: string; l: string; b?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: 'var(--text-secondary)' }}>
      <span style={{ width: 7, height: 7, borderRadius: 3, background: c, border: b ? '1px solid var(--border)' : 'none' }} />
      {l}
    </div>
  )
}

// ── Main Component ───────────────────────────────────────

export function SiteNetworkView({ siteId, onOpenDevice, initialSwitchId, highlightPort }: Props) {
  const [switches, setSwitches] = useState<SwitchInfo[]>([])
  const [portsBySwitch, setPortsBySwitch] = useState<Record<string, PortData[]>>({})
  const [loading, setLoading] = useState(true)
  const [sel, setSel] = useState<PortData | null>(null)
  const [hov, setHov] = useState<PortData | null>(null)
  const [mode, setMode] = useState<ViewMode>('status')
  const { toast } = useToast()
  const [dragVlan, setDragVlan] = useState<typeof VLANS[number] | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)
  const [activeSwitchId, setActiveSwitchId] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const devicesRes = await api.get<{ data: SiteDeviceWithSpec[] }>(
        `/sites/${siteId}/devices?limit=100`
      ).catch((err: unknown) => {
        toast(`Failed to load devices: ${err instanceof Error ? err.message : 'unknown error'}`, 'error')
        return { data: [] as SiteDeviceWithSpec[] }
      })

      const allDevices = devicesRes.data || []
      const switchDevices = allDevices.filter(d =>
        d.spec_category === 'switch' || d.device_type === 'switch'
      )

      const switchInfos: SwitchInfo[] = switchDevices.map(d => ({
        id: d.id,
        slug: d.slug,
        name: d.label || d.hostname || 'Switch',
        model: d.spec_model ? `${d.spec_brand || ''} ${d.spec_model}`.trim() : (d.device_model || d.spec_category || 'Switch'),
        ip: d.ip_address,
        mac: d.mac_address,
        uptime: null,
        firmware: d.actual_firmware,
        poeBudget: d.spec_poe_budget_watts || d.poe_budget_watts || 0,
        totalPorts: d.spec_total_ports || d.port_count || 24,
        isLive: d.status === 'online',
      }))

      setSwitches(switchInfos)
      if (switchInfos.length > 0) {
        // Pre-select the requested switch if provided, otherwise first
        const targetId = initialSwitchId && switchInfos.some(s => s.id === initialSwitchId)
          ? initialSwitchId
          : switchInfos[0].id
        setActiveSwitchId(targetId)
      }

      const portsMap: Record<string, PortData[]> = {}
      await Promise.all(switchInfos.map(async (sw) => {
        try {
          // API returns { ports: [...], summary: {...} } or a raw array
          const resp = await api.get<{ ports: PortApiRecord[]; summary: Record<string, unknown> } | PortApiRecord[]>(
            `/sites/${siteId}/devices/${sw.id}/interfaces`
          )
          const rawPorts: PortApiRecord[] = Array.isArray(resp) ? resp : (resp.ports || [])

          const portMap = new Map<number, PortApiRecord>()
          for (const p of rawPorts) {
            if (p.port_number) portMap.set(p.port_number, p)
          }

          const totalPorts = Math.max(sw.totalPorts, rawPorts.length)
          const ports: PortData[] = []
          for (let i = 1; i <= totalPorts; i++) {
            const raw = portMap.get(i)
            if (raw) {
              const isUplink = mapStatusToUplink(raw.status)
              const hasDevice = !!(raw.connected_device_id || raw.connected_device_name || raw.connected_device_mac)
              const connCategory = raw.connected_spec_category
              const deviceType = isUplink ? 'uplink' : connCategory ? mapCategoryToType(connCategory) : (hasDevice ? 'camera' : 'empty')
              // Use is_up field (from live sync) first, then fall back to status string
              const isUp = raw.is_up === 1 || raw.status === 'connected' || raw.status === 'uplink'
              const portPoe = raw.poe_power_draw || raw.poe_watts_draw || raw.connected_poe_draw || null
              // Use real rx/tx bytes for bandwidth base (convert to approximate Mbps)
              const rxMbps = raw.rx_bytes ? Math.round(raw.rx_bytes / 125000) : 0  // rough last-sample
              const bwBase = isUp ? (rxMbps > 0 ? Math.min(rxMbps, 1000) : rnd(10, 150)) : 0
              const vlanNum = raw.vlan_id ? parseInt(raw.vlan_id, 10) || null : null
              const connLabel = raw.connected_device_label || raw.connected_device_name || raw.port_label || null
              const connName = raw.connected_device_hostname || raw.connected_device_name || raw.name || null
              const connIp = raw.connected_device_ip || null

              ports.push({
                number: i,
                label: connLabel,
                name: connName,
                type: hasDevice || isUp ? deviceType : 'empty',
                ip: connIp,
                vlan: vlanNum,
                poe: portPoe,
                speed: raw.speed,
                link: isUp,
                bwBase,
                bw: genBW(bwBase, !isUp),
                pp: raw.cable_id ? { panel: 1, port: i } : null,
                cable: raw.cable_id ? `C-${String(i).padStart(3, '0')}` : null,
                deviceSlug: raw.connected_device_id || null,
                interfaceId: raw.id,
              })
            } else {
              ports.push({
                number: i,
                label: null, name: null, type: 'empty', ip: null, vlan: null,
                poe: null, speed: null, link: false, bwBase: 0,
                bw: Array(20).fill(0) as number[],
                pp: null, cable: null, deviceSlug: null, interfaceId: null,
              })
            }
          }
          portsMap[sw.id] = ports
        } catch (err: unknown) {
          toast(`Failed to load ports for ${sw.name}: ${err instanceof Error ? err.message : 'unknown error'}`, 'error')
          const totalPorts = sw.totalPorts
          portsMap[sw.id] = Array.from({ length: totalPorts }, (_, i) => ({
            number: i + 1, label: null, name: null, type: 'empty' as const,
            ip: null, vlan: null, poe: null, speed: null, link: false,
            bwBase: 0, bw: Array(20).fill(0) as number[],
            pp: null, cable: null, deviceSlug: null, interfaceId: null,
          }))
        }
      }))

      setPortsBySwitch(portsMap)

      // Auto-select highlighted port if requested (e.g. from client click)
      if (highlightPort != null) {
        const targetSwId = initialSwitchId && portsMap[initialSwitchId] ? initialSwitchId : Object.keys(portsMap)[0]
        if (targetSwId && portsMap[targetSwId]) {
          const port = portsMap[targetSwId].find(p => p.number === highlightPort && p.type !== 'empty')
          if (port) setSel(port)
        }
      }
    } catch (err: unknown) {
      toast(`Network view error: ${err instanceof Error ? err.message : 'unknown error'}`, 'error')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- highlightPort/initialSwitchId are initial values, adding them would cause refetches
  }, [siteId, toast])

  useEffect(() => { loadData() }, [loadData])

  // Live bandwidth simulation
  useEffect(() => {
    const iv = setInterval(() => {
      setPortsBySwitch(prev => {
        const next: Record<string, PortData[]> = {}
        for (const [swId, ports] of Object.entries(prev)) {
          next[swId] = ports.map(pt =>
            pt.type === 'empty' || !pt.link
              ? pt
              : { ...pt, bw: [...pt.bw.slice(1), Math.max(0, pt.bwBase + rnd(-Math.floor(pt.bwBase * 0.3), Math.floor(pt.bwBase * 0.3)))] }
          )
        }
        return next
      })
    }, 3000)
    return () => clearInterval(iv)
  }, [])

  const drop = useCallback((switchId: string, portNumber: number) => {
    if (!dragVlan) return
    setPortsBySwitch(prev => ({
      ...prev,
      [switchId]: prev[switchId].map(pt => pt.number === portNumber ? { ...pt, vlan: dragVlan.id } : pt),
    }))
    setDragVlan(null)
    setDragOver(null)
  }, [dragVlan])

  const portColor = useCallback((p: PortData): string => {
    if (mode === 'poe') return !p.poe ? '#1e2028' : p.poe > 20 ? 'var(--err)' : p.poe > 12 ? 'var(--color-power)' : 'var(--ok)'
    if (mode === 'vlan') return VLAN_COLORS[p.vlan ?? 0] || '#1e2028'
    if (mode === 'speed') return SPEED_COLORS[p.speed || ''] || '#1e2028'
    if (mode === 'bandwidth') {
      const l = p.bw?.[p.bw.length - 1] || 0
      return l > 100 ? 'var(--err)' : l > 50 ? 'var(--color-power)' : l > 10 ? 'var(--ok)' : '#1e2028'
    }
    return TYPE_COLORS[p.type] || '#1e2028'
  }, [mode])

  if (loading) {
    return (
      <div style={{ fontFamily: 'var(--font-sans)', padding: 24 }}>
        <div className="skeleton" style={{ height: 60, borderRadius: 10, marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="skeleton" style={{ height: 80, borderRadius: 10 }} />
          ))}
        </div>
        <div className="skeleton" style={{ height: 120, borderRadius: 12 }} />
      </div>
    )
  }

  if (switches.length === 0) {
    return (
      <div style={{ fontFamily: 'var(--font-sans)', padding: 24 }}>
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 12, padding: 40, textAlign: 'center',
        }}>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>No switches found</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            Add switch devices to this site to visualize port mapping.
          </div>
        </div>
      </div>
    )
  }

  const currentSwitch = switches.find(s => s.id === activeSwitchId) || switches[0]
  const ports = portsBySwitch[currentSwitch.id] || []

  const totalPoe = ports.reduce((s, p) => s + (p.poe || 0), 0)
  const connected = ports.filter(p => p.link && p.type !== 'empty').length
  const offline = ports.filter(p => !p.link && p.type !== 'empty').length
  const aggBw = ports.reduce<number[]>((a, p) => {
    if (!p.bw) return a
    return p.bw.map((v, i) => (a[i] || 0) + v)
  }, Array(20).fill(0) as number[])

  const half = Math.ceil(ports.length / 2)
  const topRow = ports.slice(0, Math.min(half, 12))
  const bottomRow = ports.slice(Math.min(half, 12), Math.min(ports.length, 24))

  return (
    <div style={S.root} className="snv-page-enter">
      <style>{CSS}</style>

      {/* Switch selector tabs if multiple switches */}
      {switches.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {switches.map(sw => (
            <button
              key={sw.id}
              onClick={() => { setActiveSwitchId(sw.id); setSel(null) }}
              style={{
                padding: '4px 12px', borderRadius: 6,
                border: `1px solid ${sw.id === currentSwitch.id ? 'var(--accent)' : 'var(--border-subtle)'}`,
                background: sw.id === currentSwitch.id ? 'rgba(200,133,10,0.12)' : 'transparent',
                color: sw.id === currentSwitch.id ? 'var(--accent)' : 'var(--text-secondary)',
                fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)',
              }}
            >
              {sw.name}
            </button>
          ))}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="snv-status-pulse" style={{ background: currentSwitch.isLive ? 'var(--ok)' : 'var(--text-disabled)', width: 8, height: 8, borderRadius: 4 }} />
            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>{currentSwitch.name}</h2>
            {currentSwitch.model && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-disabled)' }}>{currentSwitch.model}</span>}
            {currentSwitch.isLive
              ? <span className="snv-live-badge" style={S.badge}>● LIVE</span>
              : <span style={{ ...S.badge, color: 'var(--text-tertiary)', background: 'var(--bg-surface)' }}>Manual</span>
            }
            {offline > 0 && (
              <span className="snv-alert-pulse" style={{ ...S.badge, color: 'var(--err)', background: 'rgba(220,38,38,0.12)' }}>
                {offline} OFFLINE
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 12, flexWrap: 'wrap' }}>
            {([
              ['IP', currentSwitch.ip],
              ['MAC', currentSwitch.mac],
              ['Up', currentSwitch.uptime],
              ['FW', currentSwitch.firmware],
            ] as const).filter(([, v]) => v).map(([k, v], i) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {i > 0 && <span style={{ color: 'var(--border-subtle)' }}>·</span>}
                <span style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</span>
                <span style={{ fontFamily: 'var(--font-mono)' }}>{v}</span>
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Restart', 'Export', 'Locate'].map(a => (
            <button key={a} className="snv-action-btn" style={S.btn}>{a}</button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        {[
          {
            l: 'PoE Draw',
            v: `${totalPoe.toFixed(0)}W`,
            s: `/ ${currentSwitch.poeBudget}W`,
            p: Math.round(totalPoe / currentSwitch.poeBudget * 100),
            c: totalPoe / currentSwitch.poeBudget > 0.85 ? 'var(--err)' : 'var(--ok)',
          },
          {
            l: 'Connected',
            v: String(connected),
            s: `/ ${ports.length}`,
            p: Math.round(connected / ports.length * 100),
            c: 'var(--info)',
          },
          {
            l: 'Bandwidth',
            v: `${(aggBw[aggBw.length - 1] / 1000).toFixed(1)} Gbps`,
            s: 'aggregate',
            c: 'var(--accent)',
            spark: aggBw.map(v => v / 1000),
          },
          {
            l: 'Offline',
            v: String(offline),
            s: offline ? 'attention' : 'all clear',
            c: offline ? 'var(--err)' : 'var(--ok)',
          },
        ].map((s, i) => (
          <div key={i} className="snv-stat-enter" style={{ ...S.statCard, animationDelay: `${i * 80}ms` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{s.l}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 900, color: s.c, lineHeight: 1.1 }}>{s.v}</div>
            <div style={{ fontSize: 10, color: 'var(--text-disabled)', marginTop: 2 }}>{s.s}</div>
            {s.p != null && (
              <div style={{ marginTop: 6 }}>
                <div style={S.bar}>
                  <div className="snv-progress-fill" style={{ height: '100%', borderRadius: 2, width: `${s.p}%`, background: s.c }} />
                </div>
              </div>
            )}
            {s.spark && (
              <div style={{ marginTop: 6 }}>
                <Spark data={s.spark} w={100} h={24} color={s.c} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-disabled)' }}>Color:</span>
          {(['status', 'poe', 'vlan', 'speed', 'bandwidth'] as ViewMode[]).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`snv-view-btn ${mode === m ? 'active' : ''}`}
              style={{
                padding: '2px 8px', borderRadius: 5,
                border: `1px solid ${mode === m ? 'rgba(200,133,10,0.3)' : 'var(--border-subtle)'}`,
                background: mode === m ? 'rgba(200,133,10,0.12)' : 'transparent',
                color: mode === m ? 'var(--accent)' : 'var(--text-disabled)',
                fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)',
              }}
            >
              {VIEW_LABELS[m]}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 9, color: 'var(--text-disabled)', fontWeight: 600 }}>DRAG VLAN:</span>
          {VLANS.map(v => (
            <div
              key={v.id}
              draggable
              onDragStart={() => setDragVlan(v)}
              onDragEnd={() => { setDragVlan(null); setDragOver(null) }}
              className="snv-vlan-chip"
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 5,
                border: `1px solid ${v.color}50`,
                background: `${v.color}12`,
                color: v.color, fontSize: 9, fontWeight: 700,
                fontFamily: 'var(--font-mono)', cursor: 'grab',
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: 3, background: v.color }} />
              {v.id}
            </div>
          ))}
        </div>
      </div>

      {/* Switch Chassis */}
      <div className="snv-switch-chassis" style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
        borderRadius: 12, padding: '12px 12px 8px', position: 'relative', overflow: 'visible',
      }}>
        <div style={{ height: 2, background: 'linear-gradient(90deg, transparent, rgba(200,133,10,0.08), transparent)', borderRadius: 1, marginBottom: 8 }} />
        {[topRow, bottomRow].map((row, ri) => (
          <div key={ri} style={{ display: 'flex', gap: 3, alignItems: 'center', marginBottom: 3 }}>
            {row.map(p => {
              const c = portColor(p)
              const empty = p.type === 'empty'
              const isSel = sel?.number === p.number
              const isHov = hov?.number === p.number
              const isDragOver = dragOver === p.number
              return (
                <div
                  key={p.number}
                  className={`snv-port-box ${!empty ? 'has-device' : ''} ${isSel ? 'selected' : ''} ${isDragOver ? 'drag-over' : ''}`}
                  style={{
                    width: 36, height: 40, borderRadius: 5, border: '1.5px solid',
                    borderColor: isDragOver ? 'var(--accent)' : isSel ? 'var(--accent)' : isHov && !empty ? c + '80' : 'var(--border-subtle)',
                    background: empty ? 'var(--bg-base)' : c + '18',
                    boxShadow: isSel ? `0 0 14px ${c}40, inset 0 0 8px ${c}10` : 'none',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative',
                  }}
                  onClick={() => {
                    if (empty) return
                    setSel(isSel ? null : p)
                    if (!isSel && p.deviceSlug && onOpenDevice) {
                      // selection only, click "Open" in drawer for navigation
                    }
                  }}
                  onMouseEnter={() => setHov(p)}
                  onMouseLeave={() => setHov(null)}
                  onDragOver={e => {
                    if (dragVlan && !empty) { e.preventDefault(); setDragOver(p.number) }
                  }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={() => drop(currentSwitch.id, p.number)}
                >
                  <div style={{
                    fontSize: 7, fontWeight: 800, fontFamily: 'var(--font-mono)',
                    color: c, opacity: empty ? 0.3 : 0.9, lineHeight: 1,
                  }}>
                    {String(p.number).padStart(2, '0')}
                  </div>
                  {p.poe != null && p.poe > 0 && (
                    <div style={{
                      fontSize: 6, fontFamily: 'var(--font-mono)',
                      color: p.poe > 20 ? 'var(--err)' : 'var(--color-power)', lineHeight: 1, marginTop: 1,
                    }}>
                      {p.poe}W
                    </div>
                  )}
                  {p.link && !empty && (
                    <div className="snv-port-led" style={{
                      position: 'absolute', top: 2, right: 2, width: 3, height: 3, borderRadius: 2, background: 'var(--ok)',
                    }} />
                  )}
                  {!p.link && !empty && (
                    <div className="snv-port-led-off" style={{
                      position: 'absolute', top: 2, right: 2, width: 3, height: 3, borderRadius: 2, background: 'var(--err)',
                    }} />
                  )}
                  {isHov && !empty && (
                    <div className="snv-port-tooltip" style={{
                      position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                      background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                      borderRadius: 8, padding: '8px 10px', whiteSpace: 'nowrap', zIndex: 50,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.5)', pointerEvents: 'none',
                    }}>
                      <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 11, marginBottom: 2 }}>
                        {p.label || `Port ${p.number}`} {p.name ? `— ${p.name}` : ''}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-secondary)', marginBottom: 3 }}>
                        {p.ip ? p.ip : ''}{p.vlan ? `${p.ip ? ' · ' : ''}VLAN ${p.vlan}` : ''}
                      </div>
                      <Spark data={p.bw} w={70} h={16} color={p.link ? 'var(--ok)' : 'var(--err)'} anim={false} />
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-disabled)', marginTop: 1 }}>
                        {p.bw[p.bw.length - 1]} Mbps
                      </div>
                      {p.pp && (
                        <div style={{ fontSize: 8, color: 'var(--color-power)', marginTop: 1 }}>
                          PP-{p.pp.panel}:{p.pp.port} · {p.cable}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            <div style={{ marginLeft: 8 }}>
              <div className="snv-sfp-slot" style={{
                width: 36, height: 40, borderRadius: 5, border: '1px dashed var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 7, fontWeight: 700, color: 'var(--text-disabled)', fontFamily: 'var(--font-mono)',
              }}>
                SFP
              </div>
            </div>
          </div>
        ))}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.3), transparent)', marginTop: 6 }} />
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, margin: '8px 0 16px', flexWrap: 'wrap' }}>
        {mode === 'status' && Object.entries({ camera: 'Camera', networking: 'Network', access: 'Access', phone: 'Phone', uplink: 'Uplink', empty: 'Available' }).map(([k, v]) => (
          <Lg key={k} c={TYPE_COLORS[k]} l={`${v} (${ports.filter(p => p.type === k).length})`} b={k === 'empty'} />
        ))}
        {mode === 'poe' && [['var(--ok)', '<12W'], ['var(--color-power)', '12-20W'], ['var(--err)', '>20W'], ['#1e2028', 'No PoE']].map(([c, l]) => (
          <Lg key={l} c={c} l={l} b={c === '#1e2028'} />
        ))}
        {mode === 'vlan' && VLANS.map(v => (
          <Lg key={v.id} c={v.color} l={`${v.id} ${v.name}`} />
        ))}
        {mode === 'speed' && [['var(--ok)', '1G'], ['var(--info)', '2.5G'], ['var(--accent)', '10G'], ['var(--color-infra)', '100M']].map(([c, l]) => (
          <Lg key={l} c={c} l={l} />
        ))}
        {mode === 'bandwidth' && [['var(--ok)', '<50Mbps'], ['var(--color-power)', '50-100'], ['var(--err)', '>100Mbps']].map(([c, l]) => (
          <Lg key={l} c={c} l={l} />
        ))}
      </div>

      {/* Port Detail Drawer */}
      {sel && sel.type !== 'empty' && (
        <div className="snv-drawer-enter" style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 12, overflow: 'visible', marginBottom: 16,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: TYPE_COLORS[sel.type] }} />
              <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>Port {sel.number}</span>
              <span style={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', fontWeight: 700 }}>{sel.type}</span>
              {!sel.link && <span style={{ fontSize: 10, color: 'var(--err)', fontWeight: 700 }}>● OFFLINE</span>}
            </div>
            <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: 'var(--text-disabled)', fontSize: 16, cursor: 'pointer' }}>✕</button>
          </div>
          <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={S.secTitle}>CONNECTED DEVICE</div>
                <div style={{ fontSize: 14, marginBottom: 6 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 700 }}>{sel.label || '—'}</span>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{sel.name || ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {([
                    ['IP', sel.ip || '—'],
                    ['VLAN', sel.vlan != null ? String(sel.vlan) : '—'],
                    ['Speed', sel.speed || '—'],
                    ['Patch', sel.pp ? `PP${sel.pp.panel}:${sel.pp.port}` : '—'],
                  ] as const).map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k}</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)', marginTop: 1 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              {sel.poe != null && sel.poe > 0 && (
                <div>
                  <div style={S.secTitle}>PoE</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 900, color: sel.poe > 20 ? 'var(--err)' : 'var(--ok)' }}>
                      {sel.poe}W
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={S.bar}>
                        <div className="snv-progress-fill" style={{
                          height: '100%', borderRadius: 2,
                          width: `${Math.min(sel.poe / 30 * 100, 100)}%`,
                          background: sel.poe > 20 ? 'var(--err)' : 'var(--ok)',
                        }} />
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-disabled)', marginTop: 1 }}>/ 30W max</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="snv-poe-btn" style={S.smBtn}>⏻ Cycle</button>
                    <button className="snv-poe-btn" style={S.smBtn}>⊘ Disable</button>
                  </div>
                </div>
              )}
              <div>
                <div style={S.secTitle}>CONFIGURATION</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', marginBottom: 2 }}>Profile</div>
                    <select style={S.sel} defaultValue={sel.vlan ?? 1}>
                      {VLANS.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', marginBottom: 2 }}>Speed</div>
                    <select style={S.sel} defaultValue={sel.speed || 'Auto'}>
                      <option>Auto</option>
                      <option>1G</option>
                      <option>100M</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={S.secTitle}>BANDWIDTH</div>
                <div style={{ background: 'var(--bg-base)', borderRadius: 8, padding: 8 }}>
                  <Spark data={sel.bw} w={200} h={48} color={sel.link ? 'var(--ok)' : 'var(--err)'} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-disabled)' }}>60s</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text)', fontWeight: 700 }}>{sel.bw[sel.bw.length - 1]} Mbps</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-disabled)' }}>now</span>
                  </div>
                </div>
              </div>
              <CablePath port={sel} switchName={currentSwitch.name} />
              <div>
                <div style={S.secTitle}>ACTIONS</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    { label: 'Restart Port', icon: '↻' },
                    { label: 'Open Device', icon: '→', action: () => sel.deviceSlug && onOpenDevice?.(sel.deviceSlug) },
                    { label: 'Copy IP', icon: '📋' },
                    { label: 'Trace Cable', icon: '🔍' },
                  ].map(a => (
                    <button
                      key={a.label}
                      className="snv-drawer-action-btn"
                      onClick={a.action}
                      style={{
                        width: '100%', padding: '6px 10px', borderRadius: 5,
                        border: '1px solid var(--border-subtle)', background: 'transparent',
                        color: 'var(--text-secondary)', fontSize: 10, fontWeight: 500,
                        cursor: 'pointer', fontFamily: 'var(--font-sans)', textAlign: 'left',
                      }}
                    >
                      {a.icon} {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Port Table */}
      <div>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
          Port Details
        </h3>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden' }}>
          <table className="v3-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['PORT', 'DEVICE', 'NAME', 'IP', 'VLAN', 'SPEED', 'BW', 'PoE', 'PATCH', 'STATUS'].map(h => (
                  <th key={h} style={{
                    fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                    color: 'var(--text-disabled)', padding: '6px 10px', borderBottom: '1px solid var(--border-subtle)',
                    textAlign: h === 'PoE' || h === 'BW' ? 'right' : 'left',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ports.filter(p => p.type !== 'empty').map((p, i) => (
                <tr
                  key={p.number}
                  className="snv-table-row-enter"
                  style={{ animationDelay: `${i * 25}ms`, cursor: 'pointer' }}
                  onClick={() => setSel(p)}
                >
                  <td style={S.td}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text)' }}>{p.number}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 5, height: 5, borderRadius: 3, background: TYPE_COLORS[p.type] }} />
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontWeight: 600, fontSize: 10 }}>{p.label || '—'}</span>
                    </span>
                  </td>
                  <td style={S.td}>{p.name || '—'}</td>
                  <td style={S.td}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-disabled)' }}>{p.ip || '—'}</span>
                  </td>
                  <td style={S.td}>
                    {p.vlan != null ? (
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 9, padding: '1px 5px', borderRadius: 3,
                        background: (VLAN_COLORS[p.vlan] || 'var(--color-infra)') + '18',
                        color: VLAN_COLORS[p.vlan] || 'var(--color-infra)', fontWeight: 700,
                      }}>
                        {p.vlan}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-disabled)' }}>{p.speed || '—'}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <Spark data={p.bw} w={48} h={12} color={p.link ? 'var(--ok)' : 'var(--err)'} anim={false} />
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    {p.poe ? (
                      <span style={{
                        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600,
                        color: p.poe > 20 ? 'var(--err)' : p.poe > 12 ? 'var(--color-power)' : 'var(--ok)',
                      }}>
                        {p.poe}W
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)' }}>—</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {p.pp ? (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--color-power)' }}>PP{p.pp.panel}:{p.pp.port}</span>
                    ) : '—'}
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span
                        className={p.link ? 'snv-status-pulse' : 'snv-status-pulse-red'}
                        style={{ width: 5, height: 5, borderRadius: 3, background: p.link ? 'var(--ok)' : 'var(--err)' }}
                      />
                      <span style={{ fontSize: 9, color: p.link ? 'var(--ok)' : 'var(--err)' }}>{p.link ? 'Up' : 'Down'}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ports.filter(p => p.type !== 'empty').length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 12 }}>
              No connected ports
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  root: {
    fontFamily: 'var(--font-sans)', color: 'var(--text-secondary)', padding: 24, maxWidth: 1100,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14,
  },
  badge: {
    fontSize: 9, fontWeight: 700, color: 'var(--ok)', background: 'rgba(5,150,105,0.12)',
    padding: '2px 7px', borderRadius: 5,
  },
  btn: {
    padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border-subtle)',
    background: 'transparent', color: 'var(--text-secondary)', fontSize: 11,
    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)',
  },
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14,
  },
  statCard: {
    background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
    borderRadius: 10, padding: '10px 12px',
  },
  bar: {
    height: 4, background: 'var(--bg-base)', borderRadius: 2, overflow: 'hidden',
  },
  secTitle: {
    fontSize: 8, fontWeight: 800, color: 'var(--text-disabled)', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: 6,
  },
  smBtn: {
    padding: '4px 10px', borderRadius: 5, border: '1px solid var(--border-subtle)',
    background: 'transparent', color: 'var(--text-secondary)', fontSize: 10,
    fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)', flex: 1,
  },
  sel: {
    width: '100%', height: 28, padding: '0 6px', background: 'var(--bg-base)',
    border: '1px solid var(--border-subtle)', borderRadius: 5, color: 'var(--text)',
    fontSize: 10, fontFamily: 'var(--font-sans)', outline: 'none',
  },
  td: {
    fontSize: 11, color: 'var(--text-secondary)', padding: '6px 10px',
    borderBottom: '1px solid var(--border-subtle)',
  },
}

// ── CSS Animations ───────────────────────────────────────

const CSS = `
  @keyframes snvPulseGreen { 0%,100% { box-shadow: 0 0 0 0 rgba(5,150,105,0.4); } 50% { box-shadow: 0 0 0 4px rgba(5,150,105,0); } }
  @keyframes snvPulseRed { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); } 50% { box-shadow: 0 0 0 5px rgba(220,38,38,0); } }
  .snv-status-pulse { animation: snvPulseGreen 3s ease-in-out infinite; border-radius: 50%; }
  .snv-status-pulse-red { animation: snvPulseRed 1.5s ease-in-out infinite; border-radius: 50%; }
  .snv-port-box { position: relative; transition: all 0.15s cubic-bezier(0.34,1.56,0.64,1); }
  .snv-port-box.has-device { cursor: pointer; }
  .snv-port-box.has-device:hover { transform: scale(1.12) translateY(-2px); z-index: 2; }
  .snv-port-box.selected { transform: scale(1.15) translateY(-3px); z-index: 3; }
  .snv-port-box.has-device:active { transform: scale(1.05); transition-duration: 0.05s; }
  .snv-port-box.drag-over { transform: scale(1.2) translateY(-4px); border-color: var(--accent) !important; box-shadow: 0 0 16px rgba(200,133,10,0.5) !important; }
  .snv-port-led { animation: snvLedBlink 4s ease infinite; }
  @keyframes snvLedBlink { 0%,90%,100% { opacity: 1; } 95% { opacity: 0.3; } }
  .snv-port-led-off { animation: snvLedOff 1s ease infinite; }
  @keyframes snvLedOff { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
  @keyframes sparkDraw { to { stroke-dashoffset: 0; } }
  .snv-stat-enter { animation: snvStatIn 0.4s cubic-bezier(0.16,1,0.3,1) backwards; }
  @keyframes snvStatIn { from { opacity: 0; transform: translateY(12px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .snv-progress-fill { animation: snvProgGrow 0.8s cubic-bezier(0.16,1,0.3,1) forwards; }
  @keyframes snvProgGrow { from { width: 0% !important; } }
  .snv-port-tooltip { animation: snvTipIn 0.15s cubic-bezier(0.34,1.56,0.64,1); transform-origin: bottom center; }
  @keyframes snvTipIn { from { opacity: 0; transform: translateX(-50%) translateY(4px) scale(0.9); } to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); } }
  .snv-drawer-enter { animation: snvDrawIn 0.3s cubic-bezier(0.34,1.56,0.64,1); }
  @keyframes snvDrawIn { from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .snv-table-row-enter { animation: snvRowIn 0.25s cubic-bezier(0.16,1,0.3,1) backwards; }
  @keyframes snvRowIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
  .snv-table-row-enter:hover { background: var(--bg-surface); }
  .snv-view-btn { transition: all 0.15s; }
  .snv-view-btn:hover { border-color: rgba(200,133,10,0.25); color: var(--accent); }
  .snv-action-btn { transition: all 0.15s cubic-bezier(0.16,1,0.3,1); }
  .snv-action-btn:hover { border-color: rgba(200,133,10,0.25); color: var(--text); transform: translateY(-1px); box-shadow: 0 2px 8px rgba(200,133,10,0.1); }
  .snv-action-btn:active { transform: scale(0.97); }
  .snv-poe-btn { transition: all 0.12s; }
  .snv-poe-btn:hover { border-color: rgba(200,133,10,0.3); background: rgba(200,133,10,0.06); color: var(--text); }
  .snv-drawer-action-btn { transition: all 0.12s cubic-bezier(0.16,1,0.3,1); }
  .snv-drawer-action-btn:hover { background: rgba(200,133,10,0.06); border-color: rgba(200,133,10,0.2); color: var(--text); padding-left: 14px !important; }
  .snv-vlan-chip { transition: all 0.15s; user-select: none; }
  .snv-vlan-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.3); }
  .snv-switch-chassis { transition: box-shadow 0.3s; }
  .snv-switch-chassis:hover { box-shadow: 0 0 60px rgba(200,133,10,0.04), 0 8px 30px rgba(0,0,0,0.3); }
  .snv-sfp-slot { transition: all 0.2s; }
  .snv-sfp-slot:hover { border-color: var(--text-disabled) !important; color: var(--text-tertiary) !important; }
  .snv-live-badge { animation: snvLivePulse 2s ease-in-out infinite; }
  @keyframes snvLivePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
  .snv-alert-pulse { animation: snvAlertP 1.5s ease-in-out infinite; }
  @keyframes snvAlertP { 0%,100% { opacity: 1; } 50% { opacity: 0.7; } }
  @keyframes snvCablePulse { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
  .snv-page-enter { animation: snvPageIn 0.35s cubic-bezier(0.16,1,0.3,1); }
  @keyframes snvPageIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) {
    .snv-status-pulse, .snv-status-pulse-red, .snv-port-led, .snv-port-led-off,
    .snv-stat-enter, .snv-progress-fill, .snv-port-tooltip, .snv-drawer-enter,
    .snv-table-row-enter, .snv-live-badge, .snv-alert-pulse, .snv-page-enter {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`
